import React, { useEffect, useRef, useState } from 'react';
import { Job } from '../../../models/Job';
import { uploadFile } from '../../../services/uploadService';
import { AcaIcon } from '../../shared/aca';
import { AuthModal } from '../../ui/AuthModal';
import { VELLUM as T, SANS, display, mono, body, btn } from '../../shared/folio/kit';
import {
  FP,
  FieldBlock,
  FieldRow,
  ErrorNote,
  fieldInput,
  fieldArea,
  folioFormCss,
} from '../../shared/folio/form';
import { FolioSelect } from '../../shared/folio/Select';

/* Applying for a post, drawn as a sheet.

   The particulars go into the same title block every other form on the site
   uses, and the two documents are entered above it as enclosures - the thing
   a paper application would clip to the front. It was the last screen still
   set in the superseded palette, which was conspicuous because the reader
   arrives here from a job page drawn entirely in the folio.

   It also carried its own fixed overlay: no focus trap, no Escape, no scroll
   lock, no aria-modal. That is now AuthModal's job, the same dialog the
   candidate dossier and the auth forms are served in. */

const DIALOG_TITLE_ID = 'application-form-title';

const AVAILABILITY_OPTIONS = [
  { value: 'Immediately', label: 'Immediately' },
  { value: '1 month', label: '1 month notice' },
  { value: '2 months', label: '2 months notice' },
  { value: '3 months', label: '3 months notice' },
];

interface ApplicationFormProps {
  job: Job;
  onSubmit: (applicationData: ApplicationData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  hasAlreadyApplied?: boolean;
  userCvUrl?: string;
}

export interface ApplicationData {
  coverLetter: string;
  resumeUrl: string;
  availability: "Immediately" | "1 month" | "2 months" | "3 months" | "";
  expectedSalary: string;
  additionalInfo: string;
}

/** Try to extract a readable filename from a URL, fallback to generic name */
const extractFileName = (url: string, fallback: string): string => {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/');
    const last = segments[segments.length - 1];
    if (last && last.includes('.')) return decodeURIComponent(last);
  } catch {
    // not a valid URL
  }
  return fallback;
};

const sectionLabel: React.CSSProperties = { ...mono(T), marginBottom: 9 };

const hint: React.CSSProperties = {
  ...mono(T),
  fontSize: 9.5,
  letterSpacing: '0.12em',
  marginTop: 8,
};

/* An enclosure, once it is in hand: the document ruled into a box with its
   own particulars, the way the register draws any other fixed value. */
const Enclosure: React.FC<{
  name: string;
  note: string;
  chosen?: boolean;
  onRemove?: () => void;
  onSelect?: () => void;
}> = ({ name, note, chosen = true, onRemove, onSelect }) => (
  <div
    onClick={onSelect}
    className={onSelect ? `${FP}-role` : undefined}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '11px 13px',
      border: `1.5px solid ${chosen ? T.ink : T.rule}`,
      background: T.ground,
      marginBottom: 10,
      cursor: onSelect ? 'pointer' : 'default',
    }}
  >
    <span
      aria-hidden
      style={{
        width: 30,
        height: 30,
        border: `1px solid ${T.rule}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <AcaIcon name="doc" size={15} stroke={chosen ? T.ink : T.mute} />
    </span>

    <span style={{ flex: 1, minWidth: 0 }}>
      <span
        style={{
          display: 'block',
          fontFamily: SANS,
          fontSize: 14,
          fontWeight: 600,
          color: T.ink,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </span>
      <span style={{ ...mono(T), display: 'block', fontSize: 9.5, letterSpacing: '0.12em', marginTop: 3 }}>
        {note}
      </span>
    </span>

    {chosen && !onRemove && <AcaIcon name="check" size={14} stroke={T.accent} />}

    {onRemove && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        aria-label={`Remove ${name}`}
        className={`${FP}-quiet`}
        style={{
          background: 'transparent',
          border: 'none',
          padding: 4,
          cursor: 'pointer',
          display: 'flex',
          color: T.mute,
          flexShrink: 0,
        }}
      >
        <AcaIcon name="close" size={13} stroke="currentColor" />
      </button>
    )}
  </div>
);

export const ApplicationForm: React.FC<ApplicationFormProps> = ({
  job,
  onSubmit,
  onCancel,
  hasAlreadyApplied = false,
  userCvUrl,
}) => {
  const [formData, setFormData] = useState<ApplicationData>({
    coverLetter: '',
    resumeUrl: '',
    availability: '',
    expectedSalary: '',
    additionalInfo: '',
  });

  // CV state
  const [useProfileCv, setUseProfileCv] = useState(!!userCvUrl);
  const [cvFile, setCvFile] = useState<File | null>(null);

  // Sync useProfileCv when userCvUrl prop transitions from falsy to truthy on first arrival.
  // Avoids clobbering an explicit user toggle on subsequent renders.
  const profileCvArrivedRef = useRef<boolean>(!!userCvUrl);
  useEffect(() => {
    if (!profileCvArrivedRef.current && userCvUrl) {
      profileCvArrivedRef.current = true;
      setUseProfileCv(true);
    }
  }, [userCvUrl]);

  // Cover letter state
  const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track mounted state so async submit handlers don't setState after unmount.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleCvFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }
      setCvFile(file);
      setUseProfileCv(false);
      setError(null);
    }
  };

  const handleCoverLetterFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }
      setCoverLetterFile(file);
      setError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.availability) {
      setError('Please select your availability');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      setIsUploading(true);
      let coverLetterUrl = '';
      let resumeUrl = '';

      if (coverLetterFile) {
        const { url } = await uploadFile(coverLetterFile, 'cv');
        coverLetterUrl = url;
      }

      if (cvFile) {
        const { url } = await uploadFile(cvFile, 'cv');
        resumeUrl = url;
      } else if (useProfileCv && userCvUrl) {
        resumeUrl = userCvUrl;
      }

      if (isMountedRef.current) setIsUploading(false);

      await onSubmit({
        ...formData,
        coverLetter: coverLetterUrl,
        resumeUrl,
      });
    } catch (err: any) {
      console.error('Application submission failed:', err);
      if (isMountedRef.current) {
        setError(err.message || 'Failed to submit application. Please try again.');
        setIsUploading(false);
      }
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  // ── Already applied state ────────────────────────────────────────
  if (hasAlreadyApplied) {
    return (
      <AuthModal isOpen onClose={onCancel} size="sm" labelledBy={DIALOG_TITLE_ID}>
        <div style={{ padding: '34px 30px', fontFamily: SANS, textAlign: 'center' }}>
          <span
            aria-hidden
            style={{
              width: 44,
              height: 44,
              border: `1.5px solid ${T.ink}`,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 18,
            }}
          >
            <AcaIcon name="check" size={20} stroke={T.accent} />
          </span>
          <div style={{ ...mono(T, T.accent), marginBottom: 10 }}>§ Already applied</div>
          <h2 id={DIALOG_TITLE_ID} style={{ ...display(T, '26px'), marginBottom: 12 }}>
            Application on file.
          </h2>
          <p style={{ ...body(T), fontSize: 14.5, margin: '0 0 26px' }}>
            You have already applied for {job.title} at {job.company}.
          </p>
          <button type="button" onClick={onCancel} className={`${FP}-btn`} style={{ ...btn(T, 'outline') }}>
            Close
          </button>
        </div>
        <style>{folioFormCss}</style>
      </AuthModal>
    );
  }

  const profileCvName = userCvUrl ? extractFileName(userCvUrl, 'My CV') : '';

  return (
    <AuthModal isOpen onClose={onCancel} size="md" labelledBy={DIALOG_TITLE_ID}>
      <div style={{ fontFamily: SANS, color: T.ink }}>
        {/* Head. The post being applied for, named once. */}
        <div style={{ padding: '28px 30px 20px', borderBottom: `1.5px solid ${T.ink}` }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
            <h2 id={DIALOG_TITLE_ID} style={display(T, 'clamp(24px, 4vw, 30px)')}>
              {job.title}
            </h2>
            <span style={mono(T, T.accent)}>§ Application</span>
          </div>
          <div style={{ ...body(T), fontSize: 14, marginTop: 8 }}>
            {job.company} · {job.location}
          </div>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '26px 30px 30px' }}>
          {error && (
            <div style={{ marginBottom: 22 }}>
              <ErrorNote>{error}</ErrorNote>
            </div>
          )}

          {/* ── Enclosures ─────────────────────────────────────────
              The documents are entered before the particulars, because
              that is the order the reader thinks in: the CV is the
              application, the rest is annotation on it. */}
          <div style={{ marginBottom: 26 }}>
            <div style={sectionLabel}>CV / résumé</div>

            {userCvUrl && !cvFile && (
              <Enclosure
                name={profileCvName}
                note="From your profile"
                chosen={useProfileCv}
                onSelect={() => { setCvFile(null); setUseProfileCv(true); }}
              />
            )}

            {cvFile && (
              <Enclosure
                name={cvFile.name}
                note={`${(cvFile.size / 1024 / 1024).toFixed(2)} MB`}
                onRemove={() => { setCvFile(null); if (userCvUrl) setUseProfileCv(true); }}
              />
            )}

            <button
              type="button"
              onClick={() => document.getElementById('cv-input')?.click()}
              className={`${FP}-btn`}
              style={{ ...btn(T, 'outline'), padding: '10px 15px', fontSize: 10 }}
            >
              {cvFile || (useProfileCv && userCvUrl) ? 'Upload a different CV' : 'Upload CV'}
            </button>
            <input
              id="cv-input"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleCvFileChange}
              style={{ display: 'none' }}
            />
            <div style={hint}>PDF, DOC, DOCX — max 5MB</div>
          </div>

          <div style={{ marginBottom: 30 }}>
            <div style={sectionLabel}>
              Cover letter <span style={{ color: T.mute }}>— optional</span>
            </div>

            {coverLetterFile && (
              <Enclosure
                name={coverLetterFile.name}
                note={`${(coverLetterFile.size / 1024 / 1024).toFixed(2)} MB`}
                onRemove={() => setCoverLetterFile(null)}
              />
            )}

            <button
              type="button"
              onClick={() => document.getElementById('cover-letter-input')?.click()}
              className={`${FP}-btn`}
              style={{ ...btn(T, 'outline'), padding: '10px 15px', fontSize: 10 }}
            >
              {coverLetterFile ? 'Upload a different cover letter' : 'Upload cover letter'}
            </button>
            <input
              id="cover-letter-input"
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={handleCoverLetterFileChange}
              style={{ display: 'none' }}
            />
            <div style={hint}>PDF, DOC, DOCX — max 5MB</div>
          </div>

          {/* ── Particulars ───────────────────────────────────────── */}
          <FieldBlock>
            <FieldRow label="Start" htmlFor="availability" required first>
              <FolioSelect
                bare
                id="availability"
                required
                placeholder="Select availability"
                value={formData.availability}
                onChange={(v) =>
                  setFormData((prev) => ({
                    ...prev,
                    availability: v as ApplicationData['availability'],
                  }))
                }
                options={AVAILABILITY_OPTIONS}
              />
            </FieldRow>

            <FieldRow label="Salary" htmlFor="expectedSalary">
              <input
                type="text"
                id="expectedSalary"
                value={formData.expectedSalary}
                onChange={(e) => setFormData((prev) => ({ ...prev, expectedSalary: e.target.value }))}
                placeholder="£50,000 – £60,000"
                style={fieldInput}
              />
            </FieldRow>

            <FieldRow label="Anything else" htmlFor="additionalInfo" stacked>
              <textarea
                id="additionalInfo"
                value={formData.additionalInfo}
                onChange={(e) => setFormData((prev) => ({ ...prev, additionalInfo: e.target.value }))}
                placeholder="Anything the panel should read alongside your CV."
                rows={4}
                style={fieldArea}
              />
            </FieldRow>
          </FieldBlock>

          {/* The commitment, and the quiet way out beside it. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 26, flexWrap: 'wrap' }}>
            <button
              type="submit"
              disabled={isSubmitting || isUploading}
              className={`${FP}-btn`}
              style={{ ...btn(T, 'accent') }}
            >
              {isUploading ? 'Uploading…' : isSubmitting ? 'Submitting…' : 'Submit application'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className={`${FP}-quiet`}
              style={{
                ...mono(T),
                background: 'none',
                border: 'none',
                borderBottom: `1px solid ${T.rule}`,
                padding: '0 0 3px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      <style>{`
        ${folioFormCss}

        /* The profile CV is a choice, so it answers the pointer like one. */
        .${FP}-role { transition: border-color 150ms ease, background 150ms ease; }
      `}</style>
    </AuthModal>
  );
};

export default ApplicationForm;
